import type { ReactNode } from 'react';

interface DateCellProps {
  value: unknown;
  format?: 'short' | 'medium' | 'long';
}

/**
 * Date cell renderer.
 *
 * Formats dates using Intl.DateTimeFormat.
 */
export const DateCell = ({ value, format = 'medium' }: DateCellProps): ReactNode => {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }

  // Try to parse the value as a date
  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else {
    return <span className="text-muted-foreground">Invalid date</span>;
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return <span className="text-muted-foreground">Invalid date</span>;
  }

  // Format options based on format prop
  const formatOptions: Record<string, Intl.DateTimeFormatOptions> = {
    short: { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
  };

  const formatted = new Intl.DateTimeFormat('en-US', formatOptions[format]).format(date);

  return <span>{formatted}</span>;
};

DateCell.displayName = 'DataTable.DateCell';
